import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { useSelector } from "react-redux";
import { AuthState } from "../redux/features/Auth/Login/index.types";

export interface WebSocketMessage {
  event_type: string;
  timestamp?: string;
  [key: string]: unknown;
}

export type WebSocketEventCallback = (messageData: WebSocketMessage) => void;

export interface WebSocketContextType {
  connection: WebSocket | null;
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;
  currentAccountId: number | null;
  reconnectAttempts: number;
  maxReconnectAttempts: number;
  connectionId: string | null;
  connect: (accountId: number) => Promise<void>;
  disconnect: () => void;
  reconnect: () => void;
  subscribeToEvent: (eventType: string, callback: WebSocketEventCallback) => () => void;
}

interface RootState {
  auth: AuthState;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

export interface WebSocketProviderProps {
  children: ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const [connection, setConnection] = useState<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [currentAccountId, setCurrentAccountId] = useState<number | null>(null);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [reconnectAttempts, setReconnectAttempts] = useState<number>(0);
  const [maxReconnectAttempts] = useState<number>(20); // Increased to 20 attempts
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const periodicIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const periodicStartTimeRef = useRef<number | null>(null);
  const initialConnectionAttemptedRef = useRef<boolean>(false);

  // Get auth state from Redux
  const authState = useSelector((state: RootState) => state.auth) || ({} as AuthState);
  const { loggedIn, isAuthorized, account_id } = authState;

  // Event subscription system
  const subscribersRef = useRef<Map<string, Set<WebSocketEventCallback>>>(new Map());

  // Subscribe to specific event types
  const subscribeToEvent = useCallback((eventType: string, callback: WebSocketEventCallback): (() => void) => {
    if (!subscribersRef.current.has(eventType)) {
      subscribersRef.current.set(eventType, new Set());
    }
    subscribersRef.current.get(eventType)!.add(callback);

    // Return unsubscribe function
    return () => {
      const eventSubscribers = subscribersRef.current.get(eventType);
      if (eventSubscribers) {
        eventSubscribers.delete(callback);
        if (eventSubscribers.size === 0) {
          subscribersRef.current.delete(eventType);
        }
      }
    };
  }, []);

  // Process and route messages to subscribers
  const processMessage = useCallback((messageData: WebSocketMessage): void => {
    const eventType = messageData.event_type;
    if (!eventType) return;

    // Get subscribers for this event type
    const eventSubscribers = subscribersRef.current.get(eventType);
    if (eventSubscribers) {
      eventSubscribers.forEach((callback) => {
        try {
          callback(messageData);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(`Error in WebSocket event handler for ${eventType}:`, error);
        }
      });
    }
  }, []);

  // Stop periodic connection checks
  const stopPeriodicCheck = useCallback((): void => {
    if (periodicIntervalRef.current) {
      clearInterval(periodicIntervalRef.current);
      periodicIntervalRef.current = null;
      periodicStartTimeRef.current = null;
    }
  }, []);

  // Start periodic connection checks (30 seconds interval, max 30 minutes)
  const startPeriodicCheck = useCallback((): void => {
    // Don't start if already running or if connected
    if (periodicIntervalRef.current || isConnected) {
      return;
    }

    // eslint-disable-next-line no-console
    console.log("Starting periodic WebSocket connection checks every 30 seconds");
    periodicStartTimeRef.current = Date.now();

    periodicIntervalRef.current = setInterval(() => {
      const elapsedTime = Date.now() - (periodicStartTimeRef.current || 0);
      const maxDuration = 30 * 60 * 1000; // 30 minutes in milliseconds

      // Check if 30 minutes have passed
      if (elapsedTime >= maxDuration) {
        stopPeriodicCheck();
        return;
      }

      // Try to reconnect if not already connected or connecting
      if (account_id && !isConnected && !isConnecting) {
        const minutesElapsed = Math.floor(elapsedTime / 60000);
        const secondsElapsed = Math.floor((elapsedTime % 60000) / 1000);
        // eslint-disable-next-line no-console
        console.log(`Periodic check: Attempting WebSocket reconnection (${minutesElapsed}m ${secondsElapsed}s elapsed)`);
        connect(account_id);
      }
    }, 30000);
  }, [account_id, isConnected, isConnecting, stopPeriodicCheck]);

  // Connect to WebSocket
  const connect = useCallback(
    async (accountId: number): Promise<void> => {
      // Check authentication before attempting connection
      if (!loggedIn || !isAuthorized || !accountId) {
        // eslint-disable-next-line no-console
        console.error("WebSocket connection blocked: User not authenticated", {
          loggedIn,
          isAuthorized,
          accountId,
        });
        setConnectionError("User not authenticated");
        return;
      }

      // Prevent multiple simultaneous connection attempts
      if (isConnecting || isConnected) {
        // eslint-disable-next-line no-console
        console.info("WebSocket connection blocked: Already connecting or connected");
        return;
      }

      // Clear any existing timeouts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }

      try {
        setIsConnecting(true);
        setConnectionError(null);

        // Use the same domain as the current application to ensure cookies are sent
        const currentHost = window.location.host;
        const currentProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";

        // Only close existing connection if it's not healthy
        if (connection && connection.readyState === WebSocket.OPEN) {
          // Connection is healthy, reuse it
          // eslint-disable-next-line no-console
          console.log("Reusing existing healthy WebSocket connection");
          setCurrentAccountId(accountId);
          setConnectionError(null);
          setIsConnecting(false);
          return;
        } else if (connection) {
          // Connection exists but is not open, close it
          connection.close();
        }
        // Backend gets account_id from session cookie, no query params needed
        const wsUrl = import.meta.env.VITE_WS_URL || `${currentProtocol}//${currentHost}/saya/ws/`;
        // Connecting to WebSocket with same domain for cookie support

        const newWs = new WebSocket(wsUrl);

        // Set connection timeout (15 seconds)
        connectionTimeoutRef.current = setTimeout(() => {
          if (newWs.readyState === WebSocket.CONNECTING) {
            // eslint-disable-next-line no-console
            console.log("⏰ WebSocket connection timeout");
            newWs.close();
            setConnectionError("Connection timeout");
            setIsConnecting(false);
          }
        }, 15000);

        newWs.onopen = () => {
          // Clear connection timeout on successful connection
          if (connectionTimeoutRef.current) {
            clearTimeout(connectionTimeoutRef.current);
            connectionTimeoutRef.current = null;
          }
          const newConnectionId = `ws_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          setConnectionId(newConnectionId);
          setConnection(newWs);
          setCurrentAccountId(accountId);
          setConnectionError(null);
          setIsConnected(true);
          setIsConnecting(false);

          setReconnectAttempts(0); // Reset reconnect attempts on successful connection
          reconnectAttemptsRef.current = 0; // Reset ref as well
          initialConnectionAttemptedRef.current = false; // Reset guard to allow future reconnections
          stopPeriodicCheck(); // Stop periodic checks on successful connection

          // eslint-disable-next-line no-console
          console.log(`WebSocket connected with ID: ${newConnectionId}`);
        };

        newWs.onmessage = (event: MessageEvent) => {
          // If we're receiving messages, the connection is clearly working

          try {
            const messageData = JSON.parse(event.data) as WebSocketMessage;
            // eslint-disable-next-line no-console
            console.log("WebSocket message received:", messageData);

            // Process message through subscription system
            processMessage(messageData);
          } catch {
            // eslint-disable-next-line no-console
            console.log("Raw WebSocket message (not JSON):", event.data);
            // Process raw messages as well
            processMessage({
              raw_message: event.data,
              timestamp: new Date().toISOString(),
              event_type: "raw_message",
            } as WebSocketMessage);
          }
        };

        newWs.onclose = (event: CloseEvent) => {
          // Clear connection timeout
          if (connectionTimeoutRef.current) {
            clearTimeout(connectionTimeoutRef.current);
            connectionTimeoutRef.current = null;
          }

          // eslint-disable-next-line no-console
          console.log(`WebSocket closed with ID: ${connectionId}, Code: ${event.code}, Reason: ${event.reason}`);

          setConnection(null);
          setCurrentAccountId(null);
          setIsConnected(false);
          setIsConnecting(false);
          setConnectionId(null);

          // Only attempt reconnection for abnormal closures and if we haven't exceeded max attempts
          if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
            setConnectionError(`Connection closed: ${event.code}`);

            //10-second delay between reconnection attempts
            const delay = 10000;

            // eslint-disable-next-line no-console
            console.log(
              `🔄 Scheduling WebSocket reconnect in ${delay / 1000}s... (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`
            );

            // Increment attempts immediately to prevent multiple simultaneous reconnection attempts
            reconnectAttemptsRef.current += 1;
            setReconnectAttempts(reconnectAttemptsRef.current);

            reconnectTimeoutRef.current = setTimeout(() => {
              // Check if we're still not connected before attempting reconnection
              if (!isConnected && !isConnecting) {
                connect(account_id!);
              }
            }, delay);
          } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
            setConnectionError(`Max reconnection attempts (${maxReconnectAttempts}) exceeded. Starting periodic checks...`);
            // eslint-disable-next-line no-console
            console.log("Max initial reconnection attempts exceeded. Starting periodic connection checks...");
            // Start periodic checks after initial attempts fail
            startPeriodicCheck();
          }
        };

        newWs.onerror = () => {
          setConnectionError(`WebSocket connection failed`);
        };
      } catch {
        setConnectionError(`Failed to create WebSocket connection`);
        setIsConnecting(false);
      }
    },
    [
      connection,
      isConnected,
      isConnecting,
      connectionId,
      maxReconnectAttempts,
      account_id,
      loggedIn,
      isAuthorized,
      processMessage,
      stopPeriodicCheck,
      startPeriodicCheck,
    ]
  );

  // Disconnect from WebSocket
  const disconnect = useCallback((): void => {
    // Clear all timeouts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }

    if (connection) {
      connection.close();
    }
    setConnection(null);
    setCurrentAccountId(null);
    setIsConnected(false);
    setIsConnecting(false);
    setConnectionError(null);
    setReconnectAttempts(0);
    reconnectAttemptsRef.current = 0; // Reset ref as well
    initialConnectionAttemptedRef.current = false; // Reset guard
    stopPeriodicCheck(); // Stop periodic checks on disconnect
  }, [connection, stopPeriodicCheck]);

  // Manual reconnect function
  const reconnect = useCallback((): void => {
    if (account_id && !isConnecting) {
      setReconnectAttempts(0); // Reset attempts for manual reconnect
      reconnectAttemptsRef.current = 0; // Reset ref as well
      initialConnectionAttemptedRef.current = false; // Reset guard
      connect(account_id);
    }
  }, [account_id, isConnecting, connect]);

  // Auto-connect on login - connect once per account
  useEffect(() => {
    // Check if periodic checks are active
    const periodicCheckActive = periodicIntervalRef.current !== null;

    // Guard: Prevent multiple simultaneous connection attempts on mount
    // Only attempt initial connection once per session
    if (initialConnectionAttemptedRef.current && !periodicCheckActive) {
      return;
    }

    // Only attempt connection if we have all required auth state
    // Allow connection if:
    // 1. Haven't exceeded max initial attempts yet, OR
    // 2. Periodic checks are active (they handle their own reconnection)
    if (
      loggedIn &&
      isAuthorized &&
      account_id &&
      !isConnected &&
      !isConnecting &&
      (reconnectAttemptsRef.current < maxReconnectAttempts || periodicCheckActive)
    ) {
      // Only log and connect if not in periodic check phase (to avoid double connection attempts)
      if (!periodicCheckActive) {
        // eslint-disable-next-line no-console
        console.info("WebSocket: All conditions met, attempting initial connection");
        initialConnectionAttemptedRef.current = true; // Mark that we've attempted
        connect(account_id);
      }
    }
  }, [loggedIn, isAuthorized, account_id, isConnected, isConnecting, maxReconnectAttempts, connect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (connection) {
        connection.close();
      }
    };
  }, [connection]);

  // Cleanup timeouts and intervals on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
      if (periodicIntervalRef.current) {
        clearInterval(periodicIntervalRef.current);
      }
    };
  }, []);

  const contextValue: WebSocketContextType = {
    connection,
    isConnected,
    isConnecting,
    connectionError,
    currentAccountId,
    reconnectAttempts,
    maxReconnectAttempts,
    connectionId,
    connect,
    disconnect,
    reconnect,
    subscribeToEvent,
    // Removed sendMessage and sendUIEvent - UI no longer sends WebSocket messages
  };

  return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>;
};

export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};

export default WebSocketContext;
